//  ----------------- Your News ----------------- //

import { supabase, supabaseWeather } from 'Services/supabaseClient'

export async function provisionUserNewsReminders(userId, subType) {
  const { data, error } = await supabaseWeather.rpc('provision_user_news_reminders', {
    p_user_id: Number(userId),
    p_subscription_type: Number(subType)
  })

  if (error) {
    console.warn('YourNews: provision_user_news_reminders error (non-fatal):', error)

    return { provisioned_count: 0 }
  }

  return typeof data === 'number' ? { provisioned_count: data } : { provisioned_count: data?.provisioned_count ?? 0 }
}

export async function deliverPendingGlobalResponses(userId) {
  const { data, error } = await supabaseWeather.rpc('deliver_pending_global_responses', {
    p_user_id: Number(userId)
  })

  if (error) {
    console.warn('YourNews: deliver_pending_global_responses error (non-fatal):', error)

    return { delivered_count: 0 }
  }

  return typeof data === 'number' ? { delivered_count: data } : { delivered_count: data?.delivered_count ?? 0 }
}

export async function getUserNewsReminders(userId) {
  const { data, error } = await supabaseWeather.rpc('get_user_news_reminders', {
    p_user_id: Number(userId)
  })

  if (error) {
    console.warn('YourNews: get_user_news_reminders error:', error)

    return { success: false, reminders: [] }
  }
  const result = data && typeof data === 'object' ? data : {}
  let reminders = []

  if (Array.isArray(result.reminders) || (result.success && result.reminders)) {
    reminders = result.reminders
  }

  return { success: result.success !== false, reminders }
}

export async function updateNewsReminder(params) {
  const { p_reminder_id: reminderId, p_user_id: userId, p_title: title = null, p_prompt: prompt = null, p_reminder_time: reminderTime = null, p_repeat_type: repeatType = null, p_custom_days: customDays = null, p_is_active: isActive = null } = params
  const { data, error } = await supabaseWeather.rpc('update_news_reminder', {
    p_reminder_id: reminderId,
    p_user_id: Number(userId),
    p_title: title,
    p_prompt: prompt,
    p_reminder_time: reminderTime,
    p_repeat_type: repeatType,
    p_custom_days: customDays,
    p_is_active: Boolean(isActive)
  })

  if (error) {
    console.warn('YourNews: update_news_reminder error:', error)

    return { success: false, error: error.message }
  }
  const result = data && typeof data === 'object' ? data : {}

  return { success: result.success === true, reminder: result.reminder }
}

export async function reorderUserNewsReminders(userId, orderedIds) {
  const { data, error } = await supabaseWeather.rpc('reorder_user_news_reminders', {
    p_user_id: Number(userId),
    p_ordered_ids: Array.isArray(orderedIds) ? orderedIds : []
  })

  if (error) {
    console.warn('YourNews: reorder_user_news_reminders error:', error)

    return false
  }

  return data === true || (data && data.success === true)
}

export async function getNewsReminderHistory(reminderId, userId, limit = 50, offset = 0) {
  try {
    const { data, error } = await supabaseWeather.rpc('get_news_reminder_history', {
      p_reminder_id: reminderId,
      p_user_id: Number(userId),
      p_limit: Number(limit) || 50,
      p_offset: Number(offset) || 0
    })

    if (error) {
      console.warn('YourNews: get_news_reminder_history error:', error)

      return { success: false, history: [], total: 0 }
    }
    const result = data && typeof data === 'object' ? data : {}
    const history = Array.isArray(result.history) ? result.history : []

    return {
      success: result.success === true,
      history,
      total: result.total ?? 0,
      limit: result.limit ?? limit,
      offset: result.offset ?? offset
    }
  } catch (e) {
    console.warn('YourNews: get_news_reminder_history exception:', e)

    return { success: false, history: [], total: 0 }
  }
}

export async function getNewsReminderTemplates(subType) {
  const { data, error } = await supabaseWeather.rpc('get_news_reminder_templates', {
    p_sub_type: Number(subType)
  })

  if (error) {
    console.warn('YourNews: get_news_reminder_templates error:', error)

    return []
  }

  // eslint-disable-next-line
  return Array.isArray(data) ? data : (data?.templates ?? [])
}

export async function deleteNewsReminder(reminderId, userId) {
  const { data, error } = await supabaseWeather.rpc('delete_news_reminder', {
    p_reminder_id: reminderId,
    p_user_id: Number(userId)
  })

  if (error) {
    console.warn('YourNews: delete_news_reminder error:', error)

    return { success: false }
  }
  const result = data && typeof data === 'object' ? data : {}

  return { success: result.success === true }
}

export async function markNewsReminderLastRead(reminderId, userId) {
  const { data, error } = await supabaseWeather.rpc('upsert_news_reminder_last_read', {
    p_reminder_id: reminderId,
    p_user_id: Number(userId)
  })

  if (error) {
    console.warn('YourNews: upsert_news_reminder_last_read error:', error)

    return { success: false }
  }
}

export async function getLastScheduledYourNews(userId) {
  const { data, error } = await supabaseWeather.rpc('get_last_scheduled_your_news', {
    p_user_id: Number(userId)
  })

  if (error) {
    return { success: false, reminder: null }
  }

  const result = data && typeof data === 'object' ? data : {}

  const reminder = result || null

  if (!reminder || !reminder.id) {
    return { success: false, reminder: null }
  }

  return { success: true, reminder }
}

// -----------------END Your News ----------------- //
const fetchInternalCommunityMembers = async (communityId) => {
  if (!communityId?.id) {
    return []
  }

  try {
    const { data: members, error } = await supabase.rpc('get_internal_community_members', {
      p_community_id: communityId.id
    })

    if (error) {
      console.error('[fetchInternalCommunityMembers] error', error)

      return []
    }

    return members || []
  } catch (err) {
    console.error('[fetchInternalCommunityMembers] unexpected error', err)

    return []
  }
}

const fetchPersonalChatUnreadCount = async (userDetails) => {
  if (!userDetails?.id) return 0

  try {
    const { data: personalChatList } = await supabase.rpc('get_personal_chat_list', {
      p_user_id: userDetails.id
    })
    const totalPersonalUnreadCount = personalChatList?.map((chat) => chat.unread_count || 0).reduce((a, b) => a + b, 0)

    return totalPersonalUnreadCount
  } catch (err) {
    return 0
  }
}

const fetchCommunityUnreadCount = async (userDetails) => {
  if (!userDetails?.id) return 0

  try {
    const { data: communityChatList } = await supabase.rpc('get_user_communities', { p_userid: userDetails.id })

    const totalCommunityUnreadCount = communityChatList.map((community) => community.unread_count || 0).reduce((a, b) => a + b, 0)

    return totalCommunityUnreadCount
  } catch (err) {
    return 0
  }
}

//  Persists the user's community list order to the backend (fire-and-forget).

export { fetchInternalCommunityMembers, fetchPersonalChatUnreadCount, fetchCommunityUnreadCount }
